import React, { useMemo } from 'react';
import { Box, ShimmerBox, Text } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { AccentText } from '@/components';
import currentTheme from '@/styles/theme';

import { CIPStatisticTileProps, CIPStatisticTileType } from '../CIPStatistic.types';

const TEST_ID_PREFIX = 'dashboard_statistics_cip-statistic';

interface TileAppearance {
  background: string;
  hasBorder: boolean;
  dotColor?: string;
}

const TILE_APPEARANCE: Record<CIPStatisticTileType, TileAppearance> = {
  [CIPStatisticTileType.Pending]: {
    background: currentTheme.colors.warningContainer,
    hasBorder: false,
    dotColor: currentTheme.colors.warning,
  },
  [CIPStatisticTileType.Failed]: {
    // The design uses the danger container at 50% opacity.
    background: `${currentTheme.colors.dangerContainer}80`,
    hasBorder: false,
    dotColor: currentTheme.colors.danger,
  },
  [CIPStatisticTileType.Total]: {
    background: currentTheme.colors.canvas,
    hasBorder: true,
  },
};

export const CIPStatisticTile: React.FC<CIPStatisticTileProps> = ({
  type,
  testId,
  label,
  value,
  isLoading,
}) => {
  const { background, hasBorder, dotColor } = useMemo(() => TILE_APPEARANCE[type], [type]);
  if (isLoading) {
    return (
      <Box
        data-testid={`${TEST_ID_PREFIX}_${testId}-tile`}
        background="canvas"
        border={{ color: 'border2', size: '1px' }}
        round="container2Round"
        pad={{ horizontal: 'spacing12', vertical: 'spacing8' }}
        direction="column"
        justify="center"
        gap="spacing2"
        style={{ flex: 1 }}
      >
        <ShimmerBox
          data-testid={`${TEST_ID_PREFIX}_${testId}-loader`}
          round="checkboxRound"
          width="62px"
          height="36px"
        />
        <ShimmerBox round="checkboxRound" width="100%" height="20px" margin={{ top: 'spacing2' }} />
      </Box>
    );
  }
  return (
    <Box
      data-testid={`${TEST_ID_PREFIX}_${testId}-tile`}
      background={background}
      border={hasBorder ? { color: 'border2', size: '1px' } : undefined}
      round="container2Round"
      pad={{ horizontal: 'spacing12', vertical: 'spacing8' }}
      direction="column"
      justify="center"
      gap="spacing2"
      style={{ flex: 1 }}
    >
      <AccentText data-testid={`${TEST_ID_PREFIX}_${testId}-count`} size="2xl" weight={700}>
        {value === null || value === undefined ? EMPTY_FIELD_VALUE : value}
      </AccentText>
      <Box direction="row" align="center" gap="spacing4">
        {dotColor && <Box width="10px" height="10px" background={dotColor} round="50%" flex={false} />}
        <Text data-testid={`${TEST_ID_PREFIX}_${testId}-label`} size="medium" weight={400}>
          {label}
        </Text>
      </Box>
    </Box>
  );
};

export default CIPStatisticTile;
